/* eslint-disable */
import type { MediaPipeSegmenterConfig } from '../core/types'

const DEFAULT_CDN_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'

type SegmenterModel = 'selfie_segmenter' | 'selfie_multiclass_256x256'

const MODEL_URLS: Record<SegmenterModel, string> = {
  selfie_segmenter:
    'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
  selfie_multiclass_256x256:
    'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float16/latest/selfie_multiclass_256x256.tflite'
}

/**
 * Thin wrapper around MediaPipe `ImageSegmenter` from `@mediapipe/tasks-vision`.
 *
 * - Lazy-loads the WASM runtime + model from Google's CDN on first use.
 * - Uses `runningMode: 'VIDEO'` for temporal optimisation across frames.
 * - Supports GPU delegate for hardware-accelerated segmentation.
 * - Returns a confidence mask (Float32Array-backed) for smooth edge compositing.
 */
export class MediaPipeSegmenter {
  private segmenter: any = null
  private loading = false
  private config: MediaPipeSegmenterConfig

  constructor (config: Partial<MediaPipeSegmenterConfig> = {}) {
    this.config = {
      model: config.model ?? 'selfie_segmenter',
      delegate: config.delegate ?? 'GPU',
      cdnBase: config.cdnBase ?? DEFAULT_CDN_BASE
    }
  }

  async loadModel (): Promise<void> {
    if (this.segmenter || this.loading) return
    this.loading = true

    try {
      const vision = await import('@mediapipe/tasks-vision')
      const { ImageSegmenter, FilesetResolver } = vision

      const wasmFileset = await FilesetResolver.forVisionTasks(this.config.cdnBase)

      this.segmenter = await ImageSegmenter.createFromOptions(wasmFileset, {
        baseOptions: {
          modelAssetPath: MODEL_URLS[this.config.model],
          delegate: this.config.delegate
        },
        runningMode: 'VIDEO',
        outputCategoryMask: false,
        outputConfidenceMasks: true
      })
    } finally {
      this.loading = false
    }
  }

  /**
   * Run segmentation on a `VideoFrame` and return the confidence mask for
   * the person category (index 0). The caller must NOT close the returned
   * mask — it is owned by MediaPipe and recycled internally.
   */
  segment (frame: VideoFrame, timestampMs?: number): Float32Array | null {
    if (!this.segmenter) return null

    const ts = timestampMs ?? performance.now()
    const result = this.segmenter.segmentForVideo(frame as any, ts)

    if (!result || !result.confidenceMasks || result.confidenceMasks.length === 0) {
      return null
    }

    const mask = result.confidenceMasks[0]
    const data = mask.getAsFloat32Array() as Float32Array
    return data
  }

  isLoaded (): boolean {
    return this.segmenter !== null
  }

  isLoading (): boolean {
    return this.loading
  }

  getConfig (): MediaPipeSegmenterConfig {
    return { ...this.config }
  }

  updateConfig (config: Partial<MediaPipeSegmenterConfig>): void {
    const needsReload = (
      (config.model && config.model !== this.config.model) ||
      (config.delegate && config.delegate !== this.config.delegate)
    )

    this.config = { ...this.config, ...config }

    if (needsReload && this.segmenter) {
      this.destroy()
    }
  }

  destroy (): void {
    if (this.segmenter) {
      this.segmenter.close()
      this.segmenter = null
    }
  }
}
